import React, { useContext, useEffect, useState } from 'react';
import { useOwnerManualContent } from '../owner-manual/hook/use-owner-manual-content';

import './owner-manual-sitemap.scss';
import '../../../components/sections/smash-vehicle-selector-fds/smash-vehicle-selector-fds.scss';
import {
    notify,
    SlackMessages,
} from '../../../services/slack-message-service/SlackMessageService';
import ServiceHandler from '../../../services/service-handler';
import ServerContext from '../../../contexts/serverContext';
import SupportFdsWrapper from '../../components/support-fds-wrapper/support-fds-wrapper';
import { PageNotFoundView } from '../../../views/page-not-found-view/page-not-found-view';
import siteMapService from '../../services/site-map-service/site-map-service';
import {
    getModelOptions,
    getVehicleModelsForYear,
    getYearOptions,
    getYearOptionsForModel,
    VehicleYmm,
} from '../../../models/vehicle-ymm';
import AccordionItem from '../../components/AccordionItem/AccordionItem';
import { Pagination } from '../../../components/common/pagination/pagination';
import { OWNER_MANUALS, TABLET_BREAKPOINT } from '../../support-constants';
import { FdsChevron } from '../../../components/common/chevron/fds-chevron';
import { useServerData } from '../../../hooks/use-server-data';
import { useAnalytics } from '../../../hooks/use-analytics';
import DisclosuresLinkSyndicationAware from '../../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import FDSSegmentedControl from '../../../components/sections/smash-vehicle-selector-fds/segmentedControl';
import useSeoConfig from '../../../hooks/use-seo-config';
import { VehicleListServiceAem } from '../../../services/vehicle-list-service-aem/vehicle-list-service-aem';
import BreadcrumbsAndViewBanner from '../../components/breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import { OwnerManualBreadcrumbLink } from './owner-manual-breadcrumb-link';
import useFooterDisplay from '../../../hooks/use-footer-display';
import {
    displayEUFooter,
    hideEUFooter,
} from '../../../components/utils/syndicate-util/syndicate-util';
import { EU_COUNTRIES } from '../../../constants';

const sendSlackNotification = (locale: string) => {
    notify(
        SlackMessages.AEM_CONTENT_MISSING + 'Owner manual in ' + locale,
        OWNER_MANUALS.SLACK_ALERT_OWNER_MANUALS_AEM_API
    );
};
interface VehicleListAttributes {
    id: number;
    label: string | number;
    link: string;
}
const OwnerManualSitemapPage = () => {
    const serverContext = useContext(ServerContext);
    const ownerManualContent = useOwnerManualContent();
    const baseUrl = siteMapService.getBaseUrl(
        serverContext.currentLanguageRegionCode,
        serverContext.brand
    );

    serverContext.canonical = baseUrl.domain
        .concat(baseUrl.root)
        .concat(
            siteMapService
                .getPathFromRouterConfigs(
                    serverContext.currentLanguageRegionCode,
                    serverContext.brand,
                    'OwnerManualSitemapPage',
                    []
                )
                ?.toString() || ''
        );

    const currentRoot = serverContext.root
        ? serverContext.root.substring(0, serverContext.root.length - 1)
        : '';

    const [size, setSize] = useState(800);
    const vehiclesList = useServerData(
        'ownerManualsSitemap',
        async () => {
            return await new VehicleListServiceAem().getVehicles(
                serverContext.brand,
                serverContext.currentRegionCode,
                serverContext.currentLanguageRegionCode,
                true
            );
        },
        []
    )[0];
    const yearOptions: string[] = getYearOptions(vehiclesList?.data || []);
    const modelOptions: VehicleYmm[] = getModelOptions(
        vehiclesList?.data || []
    );
    const [fireEvents] = useAnalytics();
    useSeoConfig('owner-manuals-sitemap', 'owner-manuals');
    useEffect(() => {
        window.scrollTo(0, 0);
        fireEvents(
            ['owner-owner-manuals-sitemap'],
            undefined,
            { ownerManuals: 'owner-manuals-sitemap' },
            false
        );
        setSize(window.outerWidth);
        const handleResize = () => {
            setSize(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return function cleanup() {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const ownerManualFdsElement = () => {
        if (ownerManualContent) {
            const fdsElement = document.querySelector(
                '.owner-manual-content'
            ) as HTMLElement;
            return new FDSSegmentedControl(fdsElement);
        }
    };

    useEffect(() => {
        ownerManualFdsElement();
    });

    useEffect(() => {
        if (!ownerManualContent) {
            sendSlackNotification(
                ServiceHandler.AppConfigurationService.getLanguageRegionCode()
            );
        }
    }, [ownerManualContent]);
    useEffect(() => {
        if (
            serverContext.brand === 'ford' &&
            EU_COUNTRIES.includes(serverContext.currentRegionCode)
        ) {
            ownerManualContent?.sitemapAccordionSubtitleVehicleList
                ? displayEUFooter()
                : hideEUFooter();
        }
    }, [ownerManualContent]);
    const mobileOrDesktopFormat = (vehicleList: VehicleListAttributes[]) => {
        if (size < TABLET_BREAKPOINT) {
            return (
                <>
                    <Pagination
                        data={vehicleList}
                        itemsPerPage={10}
                        isNoScroll={true}
                    />
                </>
            );
        } else {
            return (
                <>
                    {vehicleList.map(vehicle => {
                        return (
                            <>
                                <div>
                                    <a href={`${currentRoot}${vehicle.link}`}>
                                        {vehicle.label}
                                    </a>
                                </div>
                            </>
                        );
                    })}
                </>
            );
        }
    };
    const renderAccordionBody = (vehicles: VehicleYmm[], year: string) => {
        const vehicleList = vehicles.map((vehicle, index) => {
            return {
                id: index,
                label: vehicle.model,
                link: siteMapService
                    .getPathFromRouterConfigs(
                        serverContext.currentLanguageRegionCode,
                        serverContext.brand,
                        'OwnerManualDetailsPage',
                        [vehicle.seoKey, year]
                    )
                    ?.replace('+', ''),
            };
        });
        return mobileOrDesktopFormat(vehicleList);
    };

    const renderAccordionBodyByYears = (years: number[], model: string) => {
        const vehicleList = years.map((year, index) => {
            return {
                id: index,
                label: year,
                link: siteMapService
                    .getPathFromRouterConfigs(
                        serverContext.currentLanguageRegionCode,
                        serverContext.brand,
                        'OwnerManualDetailsPage',
                        [model, year.toString()]
                    )
                    ?.replace('+', ''),
            };
        });
        return mobileOrDesktopFormat(vehicleList);
    };

    const buildAccordionByModelYear = yearOptions.map(year => {
        const vehicles = getVehicleModelsForYear(
            vehiclesList?.data || [],
            parseInt(year)
        );

        return (
            <>
                <AccordionItem
                    title={year}
                    subtitle={
                        ownerManualContent?.sitemapAccordionSubtitleVehicleList
                    }
                    key={year}
                    chevronLabel={ownerManualContent?.sitemapChevronLabel}
                >
                    {renderAccordionBody(vehicles, year)}
                </AccordionItem>
            </>
        );
    });

    const buildAccordionByYearModel = modelOptions.map(model => {
        const years = getYearOptionsForModel(
            vehiclesList?.data || [],
            model.seoKey
        ).reverse();
        if (years.length > 0) {
            return (
                <>
                    <AccordionItem
                        title={model.marketName}
                        subtitle={
                            ownerManualContent?.sitemapAccordionSubtitleYearList
                        }
                        id={model.year + model.seoKey}
                        chevronLabel={ownerManualContent?.sitemapChevronLabel}
                        toggle={true}
                    >
                        {renderAccordionBodyByYears(years, model.seoKey)}
                    </AccordionItem>
                </>
            );
        }
    });
    useFooterDisplay([ownerManualContent]);
    return (
        <>
            {ownerManualContent ? (
                <SupportFdsWrapper>
                    <BreadcrumbsAndViewBanner
                        currentPage={ownerManualContent.sitemapTitle}
                        previousCrumb={
                            <OwnerManualBreadcrumbLink
                                ownerManualContent={ownerManualContent}
                                serverContext={serverContext}
                                currentRoot={currentRoot}
                            />
                        }
                        hashId={'support'}
                    />

                    {ownerManualContent && (
                        <div className="owner-manual-content">
                            <div className="width-90-mobile">
                                <h1>{ownerManualContent.sitemapTitle}</h1>
                                <h2>{ownerManualContent.sitemapSubtitle}</h2>
                                <p className="owner-manual-content-1">
                                    {
                                        ownerManualContent.sitemapSubtitleDescription
                                    }
                                </p>
                            </div>

                            <div className="sitemap-buttons-container">
                                <div className="owner-manual-button-container">
                                    <span className="to-owner-label">
                                        <a
                                            href={`${currentRoot}${siteMapService.getPathFromRouterConfigs(
                                                serverContext.currentLanguageRegionCode,
                                                serverContext.brand,
                                                'OwnerManualPage',
                                                []
                                            )}`}
                                        >
                                            {
                                                ownerManualContent.sitemapToOwnerManual
                                            }
                                        </a>
                                    </span>
                                    <FdsChevron
                                        className="to-owner-manual"
                                        direction="left"
                                        type="unfilled"
                                    />
                                </div>

                                <div className=" fds-segmented-control fds-color__bg--light">
                                    <div className="fds-segmented-control__button-list-flex-container">
                                        <div className="fds-segmented-control__button-list-wrapper">
                                            <div className="fds-segmented-control__button-list">
                                                <button
                                                    className="fds-segmented-control__button"
                                                    aria-pressed="false"
                                                    aria-controls="FDSSegmentedControl_2Buttons_NoIcon_Panel1"
                                                    id="FDSSegmentedControl_2Buttons_NoIcon_Button1"
                                                >
                                                    <span className="fds-segmented-control__button-text">
                                                        {
                                                            ownerManualContent.sitemapByModelToggle
                                                        }
                                                    </span>
                                                </button>
                                                <button
                                                    className="fds-segmented-control__button"
                                                    aria-pressed="true"
                                                    aria-controls="FDSSegmentedControl_2Buttons_NoIcon_Panel2"
                                                    id="FDSSegmentedControl_2Buttons_NoIcon_Button2"
                                                >
                                                    <span className="fds-segmented-control__button-text">
                                                        {
                                                            ownerManualContent.sitemapByYearToggle
                                                        }
                                                    </span>
                                                </button>
                                            </div>
                                            <span className="fds-segmented-control__slider">
                                                <span className="fds-segmented-control__slider-text">
                                                    {
                                                        ownerManualContent.sitemapByYearToggle
                                                    }
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="fds-segmented-control__panels">
                                <div
                                    id="FDSSegmentedControl_2Buttons_NoIcon_Panel1"
                                    className="fds-segmented-control__panel owner-manuals-sitemap"
                                    aria-labelledby="FDSSegmentedControl_2Buttons_NoIcon_Button1"
                                    tabIndex={0}
                                >
                                    <div className="fds-segmented-control__panel-content">
                                        {buildAccordionByYearModel}
                                    </div>
                                </div>
                                <div
                                    id="FDSSegmentedControl_2Buttons_NoIcon_Panel2"
                                    className="fds-segmented-control__panel owner-manuals-sitemap"
                                    aria-labelledby="FDSSegmentedControl_2Buttons_NoIcon_Button2"
                                    tabIndex={0}
                                >
                                    <div className="fds-segmented-control__panel-content">
                                        {buildAccordionByModelYear}
                                    </div>
                                </div>
                            </div>
                            <DisclosuresLinkSyndicationAware />
                        </div>
                    )}
                </SupportFdsWrapper>
            ) : (
                <PageNotFoundView />
            )}
        </>
    );
};

export default OwnerManualSitemapPage;
